import { apiClient } from './client'
import type { PriorityAction } from '../posture-brief'
import type {
  ConfigurationDrift,
  ApiResponse,
} from '@/types'

/**
 * Configuration Drift queue: CRUD, approve/reject, bulk staging, priority
 * actions, audit history/export, and the WO-9 first-run baseline-review
 * ceremony. Split out of `lib/api.ts` (WO-10.4).
 */

// Configuration Drift APIs
export async function getConfigurationDrifts(params?: {
  tenantId?: string
  severity?: string
  status?: string
  fromDate?: string
  toDate?: string
  page?: number
  limit?: number
  unprocessedOnly?: boolean // When true, only returns drifts with ProcessedAt == null (not yet processed to ADO)
  // Wave 7 (R-NEW-5): 'severity' opts in to (Severity DESC, DetectedAt DESC)
  // ordering — required by HybridDriftManagementTab so the page-1 merge
  // surfaces highest-severity rows from both sources, not "newest 50".
  // Omitted = legacy recency sort.
  sortBy?: 'severity'
  // WO-3: when true, include non-RealChange (schema-phantom / noise) rows.
  // Omitted/false = customer queue shows only RealChange. Pass-through query
  // param; the ConfigurationDrift Function proxy forwards the query string.
  includeFiltered?: boolean
}): Promise<{ items: ConfigurationDrift[], total: number, page: number, limit: number }> {
  const response = await apiClient.get<ApiResponse<{
    items: ConfigurationDrift[]
    total: number
    page: number
    limit: number
  }>>('/configurationdrift', { params })
  return response.data.data
}

export async function getConfigurationDrift(id: string): Promise<ConfigurationDrift> {
  const response = await apiClient.get<ApiResponse<ConfigurationDrift>>(`/configurationdrift/${id}`)
  return response.data.data
}

export async function updateDriftStatus(id: string, status: string, comment?: string): Promise<ConfigurationDrift> {
  const response = await apiClient.patch<ApiResponse<ConfigurationDrift>>(
    `/configurationdrift/${id}/status`,
    { status, comment }
  )
  return response.data.data
}

export async function approveDrift(id: string, comment?: string): Promise<{
  data: ConfigurationDrift
  autoApprovedCount: number
  message: string
}> {
  // Approve returns a flat envelope (data + autoApprovedCount + message),
  // not the standard ApiResponse<T> wrapper, because the toast surfaces
  // both the message and the auto-approved count from a single call.
  const response = await apiClient.post<{
    data: ConfigurationDrift
    success: boolean
    message: string
    autoApprovedCount: number
  }>(`/configurationdrift/${id}/approve`, { comment })
  return response.data
}

export async function rejectDrift(id: string, comment?: string): Promise<ConfigurationDrift> {
  const response = await apiClient.post<ApiResponse<ConfigurationDrift>>(
    `/configurationdrift/${id}/reject`,
    { comment }
  )
  return response.data.data
}

// Priority Actions API — server-side ranked posture queue. Endpoint
// returns the array directly (no ApiResponse<T> envelope), matching the
// PriorityActionsController.cs Ok(actions) shape. See:
// .agents/Tasks/High Priority/operations-priority-alerts-curation.md §7.2
export async function getPriorityActions(
  tenantId: string,
  options?: {
    limit?: number
    windowDays?: number
    pillar?: 'all' | 'compliance' | 'bpa' | 'identity'
  }
): Promise<PriorityAction[]> {
  const response = await apiClient.get<PriorityAction[]>(
    `/posture/priority-actions/tenant/${tenantId}`,
    { params: options }
  )
  return response.data ?? []
}

export interface DriftAuditHistoryEntry {
  id: string
  driftId: string
  eventType: string
  triggeredBy: string
  triggeredByObjectId: string | null
  triggeredByDisplayName: string | null
  userRole: string | null
  previousStatus: string | null
  newStatus: string
  remediationSource: string | null
  comments: string | null
  eventTimestamp: string
  additionalMetadata: string | null
}

export async function getDriftAuditHistory(id: string): Promise<DriftAuditHistoryEntry[]> {
  const response = await apiClient.get<ApiResponse<DriftAuditHistoryEntry[]>>(`/configurationdrift/${id}/audit-history`)
  return response.data.data
}

// Drift Audit Export APIs
export async function downloadDriftAuditReport(
  tenantId: string,
  format: 'csv' | 'html' = 'csv',
  startDate?: string,
  endDate?: string,
  eventTypes?: string[]
): Promise<Blob> {
  const params: Record<string, string> = { format }
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  if (eventTypes?.length) params.eventTypes = eventTypes.join(',')

  const response = await apiClient.get(
    `/reports/driftaudit/${tenantId}/download`,
    {
      params,
      responseType: 'blob'
    }
  )
  return response.data
}

// DSC cross-source staging (Accept-via-Staging blueprint §3.3 / §5.1).
// Sets each drift to Status=Approved, ProcessedAt=null — the same DB effect
// as N single approves, looped server-side. This is the DSC half of the
// Hybrid tab's "Accept N" so a multi-select doesn't fan out N HTTP calls.
// The Container binds the comment as plural `comments` and returns its own
// raw envelope ({ results, summary, message }), not the ApiResponse<T>
// wrapper; DSC's apply stays `processBatchedApprovals`.
//
// PARTIAL FAILURE: the Container processes each id independently and reports
// per-row status — a row that 403s / 404s / errors comes back with
// status!=='approved' and the batch as a whole still returns 200. We surface
// the shortfall (approved/failed counts + the failed ids) so the caller can
// keep exactly the failed rows selected and toast the truth, rather than
// reading `summary.approved` as full success (silent-failure finding F2).
export async function bulkApproveDrifts(
  driftIds: string[],
  comment?: string,
): Promise<{ approved: number; failed: number; failedIds: string[] }> {
  const response = await apiClient.post<{
    results?: Array<{ id: string; status: string; error?: string }>
    summary: { approved: number; failed: number; totalApprovedCount: number }
    message: string
  }>('/configurationdrift/bulk-approve', { driftIds, comments: comment })
  const data = response.data
  const results = data.results ?? []
  // Per-row truth: any row the server didn't flip to Approved failed. Derive
  // the failed ids from the rows so a retry only re-sends what's outstanding.
  const failedIds = results
    .filter(r => r.status !== 'approved')
    .map(r => r.id)
  // Prefer the server's summary counts; fall back to the per-row tally when
  // either is absent so a malformed envelope can't read as full success.
  const approved =
    typeof data.summary?.approved === 'number'
      ? data.summary.approved
      : results.filter(r => r.status === 'approved').length
  const failed =
    typeof data.summary?.failed === 'number'
      ? data.summary.failed
      : failedIds.length
  return { approved, failed, failedIds }
}

// DSC bulk status change — thin wrapper over the SAME endpoint the DSC bulk
// bar already uses (`DriftTableWithBulkActions` posts to it inline). Used by
// the Hybrid tab's "Accept Guardian's suggestion" flow to move ExpectedChurn
// DSC rows to `Ignored` (int 6) with the human-owned reason as the audit
// comment. No new route — this reuses the existing bulk-status-change
// controller action. The Container returns a `{ message }` envelope; `count`
// is echoed back from the request since the endpoint doesn't report per-row
// results (a whole-batch failure surfaces via the thrown server message).
export async function bulkStatusChangeDrifts(
  driftIds: string[],
  newStatus: string,
  comment?: string,
): Promise<{ count: number; message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    '/configurationdrift/bulk-status-change',
    {
      driftIds,
      newStatus,
      approvedBy: 'Current User',
      comments: comment || `Bulk ${newStatus.toLowerCase()} action`,
    },
  )
  return { count: driftIds.length, message: response.data?.message }
}

export async function processBatchedApprovals(tenantId?: string): Promise<{
  message: string
  processed: number
  // Absent (not empty) when Guardian:CreatePullRequestsForBaseline=false.
  pullRequests?: Array<{
    tenantId: string
    driftCount: number
    prUrl: string
    prId: string
  }>
}> {
  const url = tenantId
    ? `/configurationdrift/process-batched-approvals?tenantId=${tenantId}`
    : '/configurationdrift/process-batched-approvals'

  const response = await apiClient.post(url)
  // Handle potential nested data structure
  return response.data?.data || response.data
}

// Baseline Refresh API
export async function refreshBaseline(tenantId: string, requestedBy: string): Promise<{
  success: boolean
  message: string
  jobId: string
  tenantId: string
}> {
  const response = await apiClient.post(`/scan/baseline-refresh/${tenantId}`, {
    requestedBy,
    confirmationAcknowledged: true
  })
  return response.data
}

// ── WO-9: first-run welcome brief (reworked from the bless-gate ceremony) ──
// A born-Hybrid tenant's baseline is the monitor snapshot taken at
// onboarding; nothing gates on approval. `getBaselineReviewStatus` reports
// whether the one-time welcome brief is still pending; `acknowledgeBaseline`
// records that the signed-in user has seen the starting point (idempotent —
// the first stamp is kept). The Container returns a raw envelope, so read
// `data.data ?? data` defensively.
export async function getBaselineReviewStatus(
  tenantId: string,
): Promise<{ tenantId: string; welcomePending: boolean }> {
  const response = await apiClient.get(`/baseline-review/${tenantId}`)
  return response.data?.data ?? response.data
}

export async function acknowledgeBaseline(tenantId: string): Promise<{
  tenantId: string
  acknowledged: boolean
  acknowledgedAt: string | null
  acknowledgedBy: string | null
}> {
  const response = await apiClient.post(`/baseline-review/${tenantId}/acknowledge`)
  return response.data?.data ?? response.data
}

export const driftApi = {
  getConfigurationDrifts,
  getConfigurationDrift,
  updateDriftStatus,
  approveDrift,
  rejectDrift,
  getPriorityActions,
  getDriftAuditHistory,
  downloadDriftAuditReport,
  bulkApproveDrifts,
  bulkStatusChangeDrifts,
  processBatchedApprovals,
  refreshBaseline,
  getBaselineReviewStatus,
  acknowledgeBaseline,
}
